import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import api, { money } from "../lib/api";
import { SectionHeader } from "../components/shared";
import { Tabs, TabsContent } from "../components/ui/tabs";
import { TableCell, TableRow } from "../components/ui/table";
import { DataTable } from "../components/DataTable";
import { BasReport } from "../components/BasReport";

function Loading() {
  return <div className="flex items-center justify-center h-40"><div className="h-7 w-7 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
}

function TrialBalance() {
  const { data, isLoading } = useQuery({ queryKey: ["trial-balance"], queryFn: async () => (await api.get("/reports/trial-balance")).data });
  if (isLoading || !data) return <Loading />;
  return (
    <DataTable
      testid="trial-balance-table"
      data={data.rows}
      rowKey={(r) => r.code}
      rowTestid="tb-row"
      columns={[
        { key: "code", header: "Code", filter: "text", cellClassName: "font-mono text-xs" },
        { key: "name", header: "Account", filter: "text", cellClassName: "text-sm font-medium" },
        { key: "type", header: "Type", filter: "select", options: ["Asset", "Liability", "Equity", "Income", "Expense"], cellClassName: "text-sm text-muted-foreground" },
        { key: "debit", header: "Debit", align: "right", numeric: true, cellClassName: "tabular text-sm", cell: (r) => (r.debit ? money(r.debit) : "—") },
        { key: "credit", header: "Credit", align: "right", numeric: true, cellClassName: "tabular text-sm", cell: (r) => (r.credit ? money(r.credit) : "—") },
      ]}
      footer={
        <TableRow className="bg-secondary font-bold hover:bg-secondary">
          <TableCell colSpan={3} className="text-sm uppercase tracking-wide">Total</TableCell>
          <TableCell className="text-right tabular text-primary">{money(data.total_debit)}</TableCell>
          <TableCell className="text-right tabular text-primary">{money(data.total_credit)}</TableCell>
        </TableRow>
      }
    />
  );
}

function StatementList({ title, rows, total, accent }) {
  return (
    <div className="border border-border bg-card">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <h3 className="text-sm font-bold uppercase tracking-wide">{title}</h3>
      </div>
      <div className="divide-y divide-border">
        {rows.map((r) => (
          <div key={r.code} className="flex items-center justify-between px-4 py-2.5">
            <span className="text-sm"><span className="font-mono text-xs text-muted-foreground mr-2">{r.code}</span>{r.name}</span>
            <span className="tabular text-sm">{money(r.amount)}</span>
          </div>
        ))}
        <div className="flex items-center justify-between px-4 py-3 bg-secondary font-bold">
          <span className="text-sm uppercase tracking-wide">Total {title}</span>
          <span className={`tabular ${accent || "text-primary"}`}>{money(total)}</span>
        </div>
      </div>
    </div>
  );
}

function ProfitLoss() {
  const { data, isLoading } = useQuery({ queryKey: ["profit-loss"], queryFn: async () => (await api.get("/reports/profit-loss")).data });
  if (isLoading || !data) return <Loading />;
  return (
    <div className="space-y-4">
      <StatementList title="Income" rows={data.income} total={data.total_income} accent="text-emerald-600" />
      <StatementList title="Expenses" rows={data.expense} total={data.total_expense} accent="text-red-600" />
      <div className="border-2 border-primary bg-primary/5 px-5 py-4 flex items-center justify-between">
        <span className="text-base font-black uppercase tracking-wide">Net Income</span>
        <span className={`text-2xl font-black tabular ${data.net_income >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="pnl-net-income">{money(data.net_income)}</span>
      </div>
    </div>
  );
}

function BalanceSheet() {
  const { data, isLoading } = useQuery({ queryKey: ["balance-sheet"], queryFn: async () => (await api.get("/reports/balance-sheet")).data });
  if (isLoading || !data) return <Loading />;
  return (
    <div className="space-y-4">
      <StatementList title="Assets" rows={data.assets} total={data.total_assets} />
      <StatementList title="Liabilities" rows={data.liabilities} total={data.total_liabilities} />
      <StatementList title="Equity" rows={[...data.equity, { code: "—", name: "Current Earnings", amount: data.net_income }]} total={data.total_equity} />
      <div className="border-2 border-primary bg-primary/5 px-5 py-4 flex items-center justify-between">
        <span className="text-sm font-bold uppercase tracking-wide">Assets = Liabilities + Equity</span>
        <span className="tabular font-bold" data-testid="bs-check">{money(data.total_assets)} = {money(data.total_liabilities + data.total_equity)} {data.balanced ? "✓" : "✗"}</span>
      </div>
    </div>
  );
}

function AgedReceivables() {
  const { data, isLoading } = useQuery({ queryKey: ["aged-receivables"], queryFn: async () => (await api.get("/reports/aged-receivables")).data });
  if (isLoading || !data) return <Loading />;
  const cols = [
    ["current", "Current"], ["d1_30", "1–30"], ["d31_60", "31–60"], ["d61_90", "61–90"], ["d90_plus", "90+"],
  ];
  return (
    <DataTable
      testid="aged-receivables-table"
      data={data.rows}
      rowKey={(r) => r.customer}
      rowTestid="aged-row"
      empty="No outstanding receivables. 🎉"
      columns={[
        { key: "customer", header: "Customer", filter: "text", cellClassName: "text-sm font-medium" },
        ...cols.map(([k, l]) => ({ key: k, header: l, align: "right", numeric: true, cellClassName: "tabular text-sm", cell: (r) => <span className={k === "d90_plus" && r[k] > 0 ? "text-red-600 font-semibold" : ""}>{r[k] ? money(r[k]) : "—"}</span> })),
        { key: "total", header: "Total", align: "right", numeric: true, cellClassName: "tabular text-sm font-bold", cell: (r) => money(r.total) },
      ]}
      footer={data.rows.length > 0 ? (
        <TableRow className="bg-secondary font-bold hover:bg-secondary">
          <TableCell className="text-sm uppercase tracking-wide">Total</TableCell>
          {cols.map(([k]) => <TableCell key={k} className="text-right tabular">{money(data.totals[k])}</TableCell>)}
          <TableCell className="text-right tabular text-primary">{money(data.totals.total)}</TableCell>
        </TableRow>
      ) : null}
    />
  );
}

function AgedPayables() {
  const { data, isLoading } = useQuery({ queryKey: ["aged-payables"], queryFn: async () => (await api.get("/reports/aged-payables")).data });
  if (isLoading || !data) return <Loading />;
  const cols = [["current", "Current"], ["d1_30", "1–30"], ["d31_60", "31–60"], ["d61_90", "61–90"], ["d90_plus", "90+"]];
  return (
    <DataTable
      testid="aged-payables-table"
      data={data.rows}
      rowKey={(r) => r.vendor}
      rowTestid="aged-payable-row"
      empty="No outstanding payables."
      columns={[
        { key: "vendor", header: "Vendor", filter: "text", cellClassName: "text-sm font-medium" },
        ...cols.map(([k, l]) => ({ key: k, header: l, align: "right", numeric: true, cellClassName: "tabular text-sm", cell: (r) => <span className={k === "d90_plus" && r[k] > 0 ? "text-red-600 font-semibold" : ""}>{r[k] ? money(r[k]) : "—"}</span> })),
        { key: "total", header: "Total", align: "right", numeric: true, cellClassName: "tabular text-sm font-bold", cell: (r) => money(r.total) },
      ]}
      footer={data.rows.length > 0 ? (
        <TableRow className="bg-secondary font-bold hover:bg-secondary">
          <TableCell className="text-sm uppercase tracking-wide">Total</TableCell>
          {cols.map(([k]) => <TableCell key={k} className="text-right tabular">{money(data.totals[k])}</TableCell>)}
          <TableCell className="text-right tabular text-primary">{money(data.totals.total)}</TableCell>
        </TableRow>
      ) : null}
    />
  );
}

function CashCalendar() {
  const { data, isLoading } = useQuery({ queryKey: ["cash-calendar"], queryFn: async () => (await api.get("/reports/cash-calendar")).data });
  if (isLoading || !data) return <Loading />;
  const groups = {};
  for (const e of data.events) {
    const key = e.date || "Undated";
    (groups[key] = groups[key] || []).push(e);
  }
  const dates = Object.keys(groups);
  let running = 0;
  return (
    <div className="space-y-4" data-testid="cash-calendar">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="border border-border bg-card p-4">
          <p className="overline-label">Expected In</p>
          <p className="text-2xl font-black tabular mt-1 text-emerald-600" data-testid="cc-inflow">{money(data.total_inflow)}</p>
        </div>
        <div className="border border-border bg-card p-4">
          <p className="overline-label">Expected Out</p>
          <p className="text-2xl font-black tabular mt-1 text-red-600" data-testid="cc-outflow">{money(data.total_outflow)}</p>
        </div>
        <div className="border-2 border-primary bg-primary/5 p-4">
          <p className="overline-label">Net Position</p>
          <p className={`text-2xl font-black tabular mt-1 ${data.net >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="cc-net">{money(data.net)}</p>
        </div>
      </div>
      {dates.length === 0 && (
        <div className="border border-border bg-card text-center py-12 text-muted-foreground">No upcoming receivables or payables.</div>
      )}
      <div className="relative border-l-2 border-border ml-3 space-y-6">
        {dates.map((d) => {
          const dayIn = groups[d].filter((e) => e.type === "inflow").reduce((s, e) => s + e.amount, 0);
          const dayOut = groups[d].filter((e) => e.type === "outflow").reduce((s, e) => s + e.amount, 0);
          running += dayIn - dayOut;
          return (
            <div key={d} className="relative pl-6" data-testid="cc-day">
              <span className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full bg-primary border-2 border-card" />
              <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                <h4 className="text-sm font-bold font-mono">{d}</h4>
                <span className="text-xs text-muted-foreground">Running net after day: <span className={`tabular font-semibold ${running >= 0 ? "text-emerald-600" : "text-red-600"}`}>{money(running)}</span></span>
              </div>
              <div className="border border-border bg-card divide-y divide-border">
                {groups[d].map((e, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5" data-testid="cc-event">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`inline-block h-2 w-2 rounded-full shrink-0 ${e.type === "inflow" ? "bg-emerald-500" : "bg-red-500"}`} />
                      <span className="text-sm font-medium truncate">{e.party}</span>
                      <span className="font-mono text-xs text-muted-foreground shrink-0">{e.doc_no}</span>
                      {e.overdue && <span className="text-[10px] uppercase font-bold text-red-600 border border-red-200 bg-red-50 px-1.5 py-0.5 rounded-sm shrink-0">Overdue</span>}
                    </div>
                    <span className={`tabular text-sm font-semibold shrink-0 ${e.type === "inflow" ? "text-emerald-600" : "text-red-600"}`}>
                      {e.type === "inflow" ? "+" : "−"}{money(e.amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Reports() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "trial";
  const setTab = (v) => { const p = new URLSearchParams(searchParams); p.set("tab", v); setSearchParams(p, { replace: true }); };
  return (
    <div className="animate-fade-up">
      <SectionHeader title="Financial Statements" subtitle="Generated from the general ledger" />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsContent value="trial"><TrialBalance /></TabsContent>
        <TabsContent value="pnl"><ProfitLoss /></TabsContent>
        <TabsContent value="bs"><BalanceSheet /></TabsContent>
        <TabsContent value="aged"><AgedReceivables /></TabsContent>
        <TabsContent value="agedpay"><AgedPayables /></TabsContent>
        <TabsContent value="cashcal"><CashCalendar /></TabsContent>
        <TabsContent value="bas"><BasReport /></TabsContent>
      </Tabs>
    </div>
  );
}
